import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Route guard that requires the current user to have a specific organization-scoped role.
 *
 * Props:
 *   - paramName: string — URL param key holding the organization id (default 'organizationId')
 *   - requireOrgAdmin: boolean — when true, only `org_admin` is allowed.
 *                              When false (default), `org_admin` OR `course_admin` is allowed.
 *   - redirectTo: string — destination on denial (default '/dashboard')
 *
 * Global app admins (user.roles includes 'admin') always pass.
 */
function OrgRoleRoute({ children, paramName = 'organizationId', requireOrgAdmin = false, redirectTo = '/dashboard' }) {
  const { user, isInitialized, isOrgAdmin, isCourseAdminInOrg } = useAuth();
  const params = useParams();
  const organizationId = params[paramName];

  if (!isInitialized) {
    return (
      <div className="loading-auth">
        <div className="loading-spinner"></div>
        <p>Checking permissions...</p>
      </div>
    );
  }

  const isGlobalAdmin = user?.roles?.some(r => r.roleName === 'admin');
  if (isGlobalAdmin) return children;

  if (!organizationId) {
    console.warn('OrgRoleRoute: missing organizationId param');
    return <Navigate to={redirectTo} replace />;
  }

  const allowed = requireOrgAdmin
    ? isOrgAdmin(organizationId)
    : isCourseAdminInOrg(organizationId);

  if (!allowed) {
    console.warn(`OrgRoleRoute: access denied for org ${organizationId} (requireOrgAdmin=${requireOrgAdmin})`);
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

export default OrgRoleRoute;
